export type SampleStatus =
  | 'success'
  | 'error'
  | 'context_limit'
  | 'time_limit'
  | 'working_limit'
  | 'message_limit'
  | 'token_limit'
  | 'cost_limit'
  | 'operator_limit'
  | 'custom_limit';

export interface SampleScore {
  scorer: string;
  value: unknown;
  value_float: number | null;
  value_nonfinite: 'nan' | 'inf' | '-inf' | null;
  // True for a provisional mid-run score, which the sample may still supersede
  // with a final one. Rendered with an `(intermediate)` marker, and excluded
  // from the score filters and sorts.
  is_intermediate: boolean;
}

export interface SampleListItem {
  // Sample identifiers
  pk: string;
  uuid: string;
  id: string;
  epoch: number;

  // Timestamps
  started_at: string | null;
  completed_at: string | null;

  // Token counts
  input_tokens: number | null;
  output_tokens: number | null;
  reasoning_tokens: number | null;
  total_tokens: number | null;
  input_tokens_cache_read: number | null;
  input_tokens_cache_write: number | null;

  // Counts
  action_count: number | null;
  message_count: number | null;

  // Timing
  working_time_seconds: number | null;
  total_time_seconds: number | null;
  generation_time_seconds: number | null;

  // Error/limit info
  error_message: string | null;
  limit: string | null;

  // Derived status
  status: SampleStatus;

  // Invalidation
  is_invalid: boolean;
  invalidation_timestamp: string | null;
  invalidation_author: string | null;
  invalidation_reason: string | null;

  // From Eval (denormalized)
  eval_id: string;
  eval_set_id: string;
  task_name: string;
  task_version: string | null;
  model: string;
  location: string;
  filename: string;
  created_by: string | null;

  // From Score — every final score, ordered by scorer; or, for a sample with no
  // final score at all, its single newest intermediate. Check `is_intermediate`
  // before presenting an entry as a result: the two never mix in one sample.
  scores: SampleScore[];
  // Set only when unambiguous: one final score, or a caller-specified scorer.
  // Always a final — an intermediate would be indistinguishable from a result
  // in this bare string pair.
  score_value: string | null;
  score_scorer: string | null;
}

export interface SamplesResponse {
  items: SampleListItem[];
  total?: number | null;
  page: number;
  limit: number;
  has_more: boolean;
}

export const STATUS_OPTIONS: { value: SampleStatus; label: string }[] = [
  { value: 'success', label: 'Success' },
  { value: 'error', label: 'Error' },
  { value: 'context_limit', label: 'Context Limit' },
  { value: 'time_limit', label: 'Time Limit' },
  { value: 'working_limit', label: 'Working Limit' },
  { value: 'message_limit', label: 'Message Limit' },
  { value: 'token_limit', label: 'Token Limit' },
  { value: 'cost_limit', label: 'Cost Limit' },
  { value: 'operator_limit', label: 'Operator Limit' },
  { value: 'custom_limit', label: 'Custom Limit' },
];
